// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../../components/layout';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useStockAdjustmentApi } from '../../../store/api/hooks/stockAdjustmentApiHook';
import ReportFilterBar from '../../../components/ReportFilterBar';
import { downloadExcel, downloadPDF } from '../../../_common/functions';
import Button from '../../../components/elements/button';
import toast from 'react-hot-toast';

function adjustmentTypeCellRenderer(params: any) {
  const val = params.value;
  if (!val) return '';
  const isAddition = val === 'Addition';
  return (
    <span style={{
      background: isAddition ? '#dcfce7' : '#fee2e2',
      color: isAddition ? '#16a34a' : '#dc2626',
      borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{val}</span>
  );
}

function adjustedQtyCellRenderer(params: any) {
  const row = params.data;
  if (!row) return '';
  const isAddition = row.adjustmentType === 'Addition';
  const display = isAddition ? `+${params.value}` : `-${params.value}`;
  return (
    <span style={{ color: isAddition ? '#16a34a' : '#dc2626', fontWeight: 600 }}>{display}</span>
  );
}

function currencyCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '--';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const StockAdjustmentReport: React.FC = () => {
  const stockAdjustmentApi = useStockAdjustmentApi();
  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility)       params.FacilityIDs    = filterParams.facility;
    if (filterParams.subFacility)    params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.adjustmentType) params.AdjustmentType = filterParams.adjustmentType;
    if (filterParams.startDate)      params.StartDate      = filterParams.startDate;
    if (filterParams.endDate)        params.EndDate        = filterParams.endDate;

    stockAdjustmentApi.getStockAdjustmentReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.totalRecord || 0);
        setFilterOptions([
          {
            key: 'facility', label: 'Facility',
            options: (d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })),
          },
          {
            key: 'subFacility', label: 'Sub Facility',
            options: (d?.subFacilities || []).filter((f: any) => f.name?.trim()).map((f: any) => ({ value: String(f.id), label: f.name })),
          },
          {
            key: 'adjustmentType', label: 'Adjustment Type',
            options: [
              { value: 'Addition',  label: 'Addition'  },
              { value: 'Reduction', label: 'Reduction' },
            ],
          },
        ]);
      },
      (error: any) => { console.error('Failed to fetch stock adjustment report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage]);

  const handleApplyFilter = (filterMap: Record<string, string>, startDate?: string, endDate?: string) => {
    const newFilters = { ...filterMap, startDate, endDate };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
  };

  const buildExportPayload = () => {
    const params: any = { PageSize: 0, PageNumber: 1 };
    if (filters.facility)       params.FacilityIDs    = filters.facility;
    if (filters.subFacility)    params.SubFacilityIDs = filters.subFacility;
    if (filters.adjustmentType) params.AdjustmentType = filters.adjustmentType;
    if (filters.startDate)      params.StartDate      = filters.startDate;
    if (filters.endDate)        params.EndDate        = filters.endDate;
    return params;
  };

  const handleExportExcel = () => {
    setIsExportingExcel(true);
    stockAdjustmentApi.ExportStockAdjustmentReportXls(
      buildExportPayload(),
      (resp: any) => {
        downloadExcel(resp, 'stock_adjustment_report');
        toast.success('Stock adjustment report exported successfully!');
        setIsExportingExcel(false);
      },
      (error: any) => {
        toast.error(error || 'Failed to export Excel');
        setIsExportingExcel(false);
      }
    );
  };

  const handleExportPdf = () => {
    setIsExportingPdf(true);
    stockAdjustmentApi.ExportStockAdjustmentReportPdf(
      buildExportPayload(),
      (resp: any) => {
        downloadPDF(resp, 'stock_adjustment_report');
        toast.success('Stock adjustment report PDF exported successfully!');
        setIsExportingPdf(false);
      },
      (error: any) => {
        toast.error(error || 'Failed to export PDF');
        setIsExportingPdf(false);
      }
    );
  };

  const columnDefs = [
    { headerName: 'Part Name',       field: 'partsName',         headerClass: 'bg-[#f0f0f0]', minWidth: 200, flex: 1, cellRenderer: titleCell,                    headerTooltip: 'Part Name' },
    { headerName: 'Part Code',       field: 'partsCode',         headerClass: 'bg-[#f0f0f0]', width: 140,            cellRenderer: titleCell,                    headerTooltip: 'Part Code' },
    { headerName: 'Facility',        field: 'facilityName',      headerClass: 'bg-[#f0f0f0]', width: 180,            cellRenderer: titleCell,                    headerTooltip: 'Facility' },
    { headerName: 'Sub Facility',    field: 'subFacilityName',   headerClass: 'bg-[#f0f0f0]', width: 140,            cellRenderer: titleCell,                    headerTooltip: 'Sub Facility' },
    { headerName: 'Date Adjusted',   field: 'stockAdjustmentDate', headerClass: 'bg-[#f0f0f0]', width: 140,          cellRenderer: titleCell,                    headerTooltip: 'Date Adjusted' },
    { headerName: 'Adjustment Type', field: 'adjustmentType',    headerClass: 'bg-[#f0f0f0]', width: 150,            cellRenderer: adjustmentTypeCellRenderer,   headerTooltip: 'Adjustment Type' },
    { headerName: 'Adjusted Qty',    field: 'adjustedQty',       headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: adjustedQtyCellRenderer, headerTooltip: 'Adjusted Quantity' },
    { headerName: 'Unit Price',      field: 'unitPrice',         headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: currencyCellRenderer, headerTooltip: 'Per Unit Price' },
    { headerName: 'Net Total',       field: 'netTotal',          headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCellRenderer, headerTooltip: 'Net Total' },
    { headerName: 'Adjusted By',     field: 'adjustedByName',    headerClass: 'bg-[#f0f0f0]', width: 150,            cellRenderer: titleCell,                    headerTooltip: 'Adjusted By' },
    { headerName: 'Notes',           field: 'notes',             headerClass: 'bg-[#f0f0f0]', width: 200,            cellRenderer: titleCell,                    headerTooltip: 'Notes' },
  ];

  return (
    <Layout>
      <Header title="Stock Adjustment Report" back="/reports">
        <Header.Right>
          <Button theme='light' onClick={handleExportPdf} disabled={isExportingPdf}>
            {isExportingPdf ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button theme='primary' onClick={handleExportExcel} disabled={isExportingExcel}>
            {isExportingExcel ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        <ReportFilterBar availableFilters={filterOptions} onApply={handleApplyFilter} />
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#16a34a', fontWeight: 600 }}>
              {summary.totalAdditions} Additions: +${Number(summary.additionsValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#dc2626', fontWeight: 600 }}>
              {summary.totalReductions} Reductions: -${Number(summary.reductionsValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#111827', fontWeight: 600 }}>
              Net Change: {summary.additionsValue >= summary.reductionsValue ? '+' : '-'}${Math.abs(Number(summary.additionsValue || 0) - Number(summary.reductionsValue || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockAdjustmentReport;
